import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { Button } from '../../components/common/Button';
import { BackButton } from '../../components/common/BackButton';
import { AuthErrorBanner } from '../../components/shared/AuthErrorBanner';
import './ProductsPageHeader.css';
import './ProductSearchPage.css';

interface AmazonAccount {
  id: string;
  marketplace_id: string;
  seller_id: string;
  status: string;
}

interface AuthError {
  error?: string;
  message?: string;
  action?: 'reauthenticate' | 'contact_admin';
  redirect_url?: string;
}

const SELECTED_ACCOUNT_KEY = 'selectedAmazonAccountId';

export default function AsinSearchPage() {
  const navigate = useNavigate();
  const [asin, setAsin] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<AuthError | null>(null);

  // Amazon Seller Accountsを取得
  const { data: accountsData } = useQuery({
    queryKey: ['amazon-accounts'],
    queryFn: async () => {
      const response = await apiClient.get('/amazon/accounts');
      return response.data;
    },
  });

  const accounts: AmazonAccount[] = accountsData?.accounts || [];

  // 選択されたアカウントの状態を取得
  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);
  const isAccountExpired = selectedAccount?.status === 'expired';

  // 初回ロード時にlocalStorageから前回選択したアカウントを復元
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      const savedAccountId = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      if (savedAccountId && accounts.find((acc) => acc.id === savedAccountId)) {
        setSelectedAccountId(savedAccountId);
      } else {
        // 保存されたアカウントが無効な場合、最初のアカウントを選択
        setSelectedAccountId(accounts[0].id);
        localStorage.setItem(SELECTED_ACCOUNT_KEY, accounts[0].id);
      }
    }
  }, [accounts, selectedAccountId]);

  // アカウントが期限切れの場合、authErrorをセット
  useEffect(() => {
    if (isAccountExpired) {
      // 現時点では全ユーザーをadminとして扱う（TODO: 実際のユーザーロールを取得）
      setAuthError({
        error: 'Amazon認証の有効期限が切れています',
        message: 'Amazon Seller Centralとの接続を再認証する必要があります。',
        action: 'reauthenticate',
        redirect_url: '/connections/amazon'
      });
    } else {
      setAuthError(null);
    }
  }, [isAccountExpired]);

  // アカウント選択が変更されたらlocalStorageに保存
  const handleAccountChange = (accountId: string) => {
    setSelectedAccountId(accountId);
    localStorage.setItem(SELECTED_ACCOUNT_KEY, accountId);
  };

  const handleStartAuth = () => {
    // OAuth認証ページに遷移
    navigate('/amazon/auth');
  };

  const handleFetchProduct = async () => {
    if (!asin.trim()) {
      setError('ASINを入力してください');
      return;
    }

    if (!selectedAccountId) {
      setError('Amazon Seller Accountを選択してください');
      return;
    }

    // アカウントが期限切れの場合は検索を実行しない
    if (isAccountExpired) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await apiClient.get(
        `/amazon/products/${asin.trim()}?account_id=${selectedAccountId}`
      );
      // 商品取得成功後、詳細ページに遷移
      navigate(`/products/amazon/${asin.trim()}`);
    } catch (err: any) {
      // 認証エラーの場合
      if (err.response?.status === 401 && err.response?.data?.action) {
        setAuthError(err.response.data);
        setError(null);
      } else {
        setError(err.response?.data?.error || '商品情報の取得に失敗しました');
        setAuthError(null);
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="product-search-page">
      <div className="header-actions">
        <BackButton to="/products/amazon" />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">ASINから商品を取得</h1>
          <p className="page-description">特定のASINの商品情報を取得して、商品リストに追加します</p>
        </div>
        {accounts.length > 0 && (
          <div className="page-header-actions">
            <div className="account-selector-section">
              <div className="account-selector">
                <label htmlFor="account-select">Amazon Seller Account</label>
                <br />
                <select
                  id="account-select"
                  value={selectedAccountId}
                  onChange={(e) => handleAccountChange(e.target.value)}
                  className="form-select"
                >
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.seller_id} ({account.marketplace_id})
                    </option>
                  ))}
                </select>
              </div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/connections/amazon');
                }}
                className="text-link"
              >
                <img src="/cog.svg" alt="" className="text-link-icon" />
                接続管理
              </a>
            </div>
          </div>
        )}
      </header>

      <section className="search-section">
        {accounts.length === 0 ? (
          <div className="account-register-form">
            <h3>Amazon Seller Account連携</h3>
            <p className="form-description">
              Amazon Sellerアカウントと連携して、商品情報を取得できます
            </p>
            <div className="form-info-box">
              <p>
                安全なOAuth認証を使用してAmazonアカウントと連携します。
                アプリケーションに必要な権限のみを付与できます。
              </p>
            </div>
            <div className="form-actions">
              <Button
                type="button"
                onClick={handleStartAuth}
                variant="primary"
                size="md"
              >
                Amazonアカウントと連携
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* 認証エラーバナー */}
            {authError && <AuthErrorBanner error={authError} />}

            {/* 一般エラーメッセージ */}
            {error && !authError && (
              <div className="error-message" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
                {error}
              </div>
            )}

            <div className="main-section">
              <div className="asin-search-form">
            <div className="form-group-inline">
              <label htmlFor="asin-input">ASIN</label>
              <input
                id="asin-input"
                type="text"
                value={asin}
                onChange={(e) => setAsin(e.target.value)}
                placeholder="例: B0XXXXXXXXX"
                className="form-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFetchProduct();
                  }
                }}
              />
              <Button
                type="button"
                onClick={handleFetchProduct}
                disabled={isLoading || !selectedAccountId || isAccountExpired}
                variant="primary"
                size="md"
              >
                {isLoading ? '取得中...' : '検索して取り込む'}
              </Button>
            </div>
          </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
